/**
 * Shared reference formatting for the doc-engine.
 *
 * Formats `TReferenceData` into HTML. Used by both the diff renderer
 * (renderSpecialNode.ts) and single-version content rendering (renderContent.ts).
 *
 * Self-refs and bookmark refs produce `<a href="#id">` for anchor navigation.
 * Cross-doc refs produce `<a href="/{itemPath}/id" target="_blank">` for navigation.
 * Error/inactive refs produce `<span>`.
 *
 * IMPORTANT: This file must have ZERO product imports - only engine imports.
 */
import { ID_ATTR } from '../schema';
import { escapeHtml } from '../renderer/html';
import type { TDocDiffOptions, TReferenceData } from './types';
import { DELETED_SECTION_FALLBACK } from '../content/constants';

/** Build an HTML attribute string with all values escaped. */
export const buildAttrs = (attrs: Record<string, string | undefined>): string =>
  Object.entries(attrs)
    .filter((entry): entry is [string, string] => entry[1] != null)
    .map(([k, v]) => ` ${k}="${escapeHtml(v)}"`)
    .join('');

/** Build a cross-doc navigation URL: `/{itemPath}/{id}?view={version}&elem={heading}`. */
const buildCrossDocHref = (
  itemId: string,
  itemPath: string,
  versionMode: string,
  versionId?: string,
  headingId?: string
): string => {
  const view = versionMode === 'SPECIFIC' && versionId ? encodeURIComponent(versionId) : 'latest';
  let href = `/${itemPath}/${encodeURIComponent(itemId)}?view=${view}`;
  if (headingId) href += `&elem=${encodeURIComponent(headingId)}`;
  return href;
};

/**
 * Formats reference data into HTML.
 *
 * Navigable refs (self-refs, bookmark refs) produce `<a href="#targetId">`
 * so they work in both FE changes mode (click handler) and BE PDF (native anchors).
 */
export const formatReferenceHtml = (
  data: TReferenceData,
  attrs: Readonly<Record<string, string>>,
  opts: TDocDiffOptions
): string => {
  const refId = attrs[ID_ATTR] ?? '';
  const versionMode = attrs['data-version-mode'] ?? '';
  const headingId = attrs['data-heading-id'] ?? '';
  const bookmarkId = attrs['data-bookmark-id'] ?? '';
  const isSelf = versionMode === 'SELF';

  const refIdAttr = buildAttrs({ [ID_ATTR]: refId });

  // Bookmark refs - navigable via <a href="#bookmarkId">
  if (data.bookmarkPrefix) {
    return `<a href="#${escapeHtml(bookmarkId)}"${refIdAttr} class="ref-success unstyled">${escapeHtml(data.bookmarkPrefix)}</a>`;
  }
  if (data.bookmarkOriginalText) {
    return `<span${refIdAttr} class="ref-error">${escapeHtml(data.bookmarkOriginalText)}</span>`;
  }

  // H5 ref rendering: heading-only display (no item name, no arrow separator).
  // H5 refs are identified by numbering depth 6 (e.g. "1.2.3.4.5.1").
  const isH5Ref = opts.isH5RefEnabled && data.headingData?.numbering.split('.').length === 6;
  if (isH5Ref) {
    const headingText = escapeHtml(data.headingData?.text.trim() || DELETED_SECTION_FALLBACK);
    if (!data.itemActive) {
      return `<span${refIdAttr} class="ref-error">${headingText}</span>`;
    }
    if (data.headingData?.fallbackVersionId) {
      const itemPart = isSelf ? '' : `<span${refIdAttr} class="ref-success"></span>`;
      return `${itemPart}<span class="ref-error">${headingText}</span>`;
    }
    if (isSelf) {
      return `<a href="#${escapeHtml(headingId)}"${refIdAttr} class="ref-success unstyled">${headingText}</a>`;
    }
    if (data.itemId && data.itemPath) {
      const href = buildCrossDocHref(data.itemId, data.itemPath, versionMode, data.versionId, headingId);
      return `<a href="${href}" target="_blank"${refIdAttr} class="ref-success unstyled">${headingText}</a>`;
    }
    return `<span${refIdAttr} class="ref-success">${headingText}</span>`;
  }

  // Standard ref: build item text + heading text
  let itemText = escapeHtml(data.itemName);
  if (versionMode === 'SPECIFIC' && data.versionId) {
    itemText += ` (${data.versionId === 'draft' ? '' : 'v'}${escapeHtml(data.versionId)})`;
  }

  let headingText = '';
  if (headingId) {
    itemText += ' \u203A ';
    if (data.headingData) {
      headingText = `${escapeHtml(data.headingData.numbering)} ${escapeHtml(data.headingData.text.trim())}`;
    } else {
      headingText = escapeHtml(DELETED_SECTION_FALLBACK);
    }
  }

  if (!data.itemActive) {
    return `<span${refIdAttr} class="ref-error">${itemText}${headingText}</span>`;
  }

  if (headingId && (!data.headingData || data.headingData.fallbackVersionId)) {
    const itemPart = isSelf ? '' : `<span${refIdAttr} class="ref-success">${itemText}</span>`;
    return `${itemPart}<span class="ref-error">${headingText}</span>`;
  }

  // Self-refs - navigable via <a href="#headingId">
  if (isSelf) {
    if (!headingId) return `<span${refIdAttr} class="ref-success">${itemText}</span>`;
    return `<a href="#${escapeHtml(headingId)}"${refIdAttr} class="ref-success unstyled">${headingText}</a>`;
  }

  // Cross-doc refs - navigable link when itemId is available, plain text otherwise
  if (data.itemId && data.itemPath) {
    const href = buildCrossDocHref(data.itemId, data.itemPath, versionMode, data.versionId, headingId);
    return `<a href="${href}" target="_blank"${refIdAttr} class="ref-success unstyled">${itemText}${headingText}</a>`;
  }
  return `<span${refIdAttr} class="ref-success">${itemText}${headingText}</span>`;
};
